import { MemoEditor } from '@/components/notes/memo-editor';
import { NoteThread } from '@/components/notes/note-thread';
import {
  type NotesPresentation,
  setNotesPresentation,
  useNotesPresentation,
} from '@/lib/matrix-notes-prefs-store';
import { noteErrorMessage } from '@/lib/note-format';
import {
  type NoteViewer,
  type NotesScope,
  useMatrixNotes,
  useNoteActions,
} from '@/lib/notes-queries';
import type {
  Dimension,
  NoteTarget,
  NoteThread as NoteThreadData,
  SingularityLevier,
} from '@mss_creator/shared';
import { Button, Icon } from '@pms-digital/design-system';
import { type ReactNode, useState } from 'react';
import { NotesViewCard } from './notes-view-card';
import { NotesViewEmptyState } from './notes-view-empty-state';
import { NotesViewFilters } from './notes-view-filters';
import {
  authorOptions,
  buildGridOrder,
  filterThreads,
  hasActiveFilters,
  sortThreads,
  statusSummary,
} from './notes-view-logic';
import {
  DEFAULT_NOTES_SORT_DIRECTIONS,
  type NotesSort,
  type NotesViewSearch,
  type NotesViewSettings,
  resolveNotesViewSettings,
  toNotesViewSearch,
} from './notes-view-search';
import { NotesViewTable } from './notes-view-table';

export interface NotesViewProps {
  matrixId: string;
  companyId: string;
  /** Named in the memo's empty state and in « Visible par ». */
  companyName: string;
  viewer: NoteViewer;
  /** The rows of the grid, for « Ordre de la grille ». */
  singularitySetup: SingularityLevier[];
  dimensions: Dimension[];
  /** The URL's search params: filters, sort and presentation. */
  search: NotesViewSearch;
  /** The page puts the new params in the URL. */
  onSearchChange: (search: NotesViewSearch) => void;
  /** « Voir dans la grille »: the page leaves the view and opens the notes of the spot. */
  onSeeInGrid: (target: NoteTarget) => void;
  /** The clock of the relative dates; tests pin it. */
  now?: Date;
}

/**
 * The Notes view of a matrix, `?vue=notes` (planche 12): the memo, then every
 * discussion the viewer may see, as cards or as a table, filtered, searched and
 * sorted. The settings live in the URL, so the page owns the navigation; only
 * the cards / table choice is also remembered per user.
 */
export function NotesView({
  matrixId,
  companyId,
  companyName,
  viewer,
  singularitySetup,
  dimensions,
  search,
  onSearchChange,
  onSeeInGrid,
  now,
}: NotesViewProps) {
  const scope: NotesScope = { type: 'matrix', matrixId };
  const notes = useMatrixNotes(matrixId);
  const actions = useNoteActions(scope, companyId);
  const storedPresentation = useNotesPresentation(viewer.id);
  const settings = resolveNotesViewSettings(search, storedPresentation);
  const [openThreadId, setOpenThreadId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const threads = notes.data?.threads ?? [];
  const visible = sortThreads(
    filterThreads(threads, settings),
    settings,
    buildGridOrder(singularitySetup, dimensions),
  );
  const openThread: NoteThreadData | null =
    settings.presentation === 'tableau'
      ? (threads.find((thread) => thread.id === openThreadId) ?? null)
      : null;

  function change(patch: Partial<NotesViewSettings>) {
    onSearchChange(toNotesViewSearch({ ...settings, ...patch }, storedPresentation));
  }

  function changePresentation(presentation: NotesPresentation) {
    setNotesPresentation(viewer.id, presentation);
    // The new choice is the stored one: the URL need not repeat it.
    onSearchChange(toNotesViewSearch({ ...settings, presentation }, presentation));
  }

  function sortFromHeader(sort: NotesSort) {
    if (sort === settings.sort) {
      change({ dir: settings.dir === 'asc' ? 'desc' : 'asc' });
    } else {
      change({ sort, dir: DEFAULT_NOTES_SORT_DIRECTIONS[sort] });
    }
  }

  async function toggleResolved(thread: NoteThreadData) {
    setActionError(null);
    try {
      await (thread.resolvedAt === null ? actions.resolve(thread.id) : actions.reopen(thread.id));
    } catch (failure) {
      setActionError(noteErrorMessage(failure, "L'action sur la discussion a échoué. Réessayez."));
    }
  }

  function resetFilters() {
    onSearchChange(
      toNotesViewSearch(
        {
          ...settings,
          status: 'ouvertes',
          levels: [],
          authors: [],
          targetTypes: [],
          q: '',
        },
        storedPresentation,
      ),
    );
  }

  const noThreadAtAll = threads.length === 0;
  const onlyResolvedLeft =
    !noThreadAtAll &&
    settings.status === 'ouvertes' &&
    !hasActiveFilters(settings) &&
    threads.every((thread) => thread.resolvedAt !== null);

  let content: ReactNode;
  if (notes.isPending) {
    content = <p className="text-gray-600 text-small">Chargement des notes…</p>;
  } else if (notes.isError) {
    content = (
      <p role="alert" className="text-rouge-deep text-small">
        Les notes n'ont pas pu être chargées.{' '}
        <button
          type="button"
          onClick={() => void notes.refetch()}
          className="font-semibold underline underline-offset-2"
        >
          Réessayer
        </button>
      </p>
    );
  } else if (visible.length === 0) {
    content = (
      <NotesViewEmptyState
        kind={noThreadAtAll ? 'none' : onlyResolvedLeft ? 'all-resolved' : 'filtered'}
        onReset={resetFilters}
        onShowResolved={() => change({ status: 'resolues' })}
      />
    );
  } else if (settings.presentation === 'tableau') {
    content = (
      <div className="flex items-start gap-4">
        <NotesViewTable
          threads={visible}
          sort={settings.sort}
          dir={settings.dir}
          onSort={sortFromHeader}
          selectedId={openThread?.id ?? null}
          onSelect={setOpenThreadId}
          onToggleResolved={(thread) => void toggleResolved(thread)}
          {...(now ? { now } : {})}
        />
        {openThread && (
          <aside
            aria-label="Discussion"
            className="sticky top-0 flex w-[380px] shrink-0 flex-col gap-2 rounded-lg border border-gray-200 bg-white p-3"
          >
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => onSeeInGrid(openThread.target)}
                className="rounded-sm text-bleu-nuit text-caption underline underline-offset-2 hover:text-bleu-nuit-deep focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bleu-nuit"
              >
                Voir dans la grille
              </button>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label="Fermer la discussion"
                onClick={() => setOpenThreadId(null)}
              >
                <Icon name="close" size={16} />
              </Button>
            </div>
            <NoteThread
              key={openThread.id}
              thread={openThread}
              viewer={viewer}
              companyName={companyName}
              actions={actions}
              showTarget
              defaultExpanded
              className="border-0 px-0 py-0"
              {...(now ? { now } : {})}
            />
          </aside>
        )}
      </div>
    );
  } else {
    content = (
      <ul className="flex flex-col gap-3">
        {visible.map((thread) => (
          <li key={thread.id}>
            <NotesViewCard
              thread={thread}
              viewer={viewer}
              companyName={companyName}
              actions={actions}
              onSeeInGrid={() => onSeeInGrid(thread.target)}
              {...(now ? { now } : {})}
            />
          </li>
        ))}
      </ul>
    );
  }

  return (
    <div
      data-testid="notes-view"
      className="flex min-h-0 flex-1 flex-col overflow-y-auto px-6 pb-8"
    >
      <div className="mx-auto flex w-full max-w-[1120px] flex-col gap-4 pt-2">
        {notes.data && (
          <MemoEditor
            matrixId={matrixId}
            companyId={companyId}
            companyName={companyName}
            memo={notes.data.memo}
            {...(now ? { now } : {})}
          />
        )}
        <NotesViewFilters
          settings={settings}
          authors={authorOptions(threads)}
          role={viewer.role}
          onChange={change}
          onPresentationChange={changePresentation}
        />
        {notes.data && <p className="text-caption text-gray-600">{statusSummary(threads)}</p>}
        {actionError && (
          <p role="alert" className="font-semibold text-caption text-rouge-deep">
            {actionError}
          </p>
        )}
        {content}
      </div>
    </div>
  );
}
